<script setup lang="ts">
import { onErrorCaptured, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { trackException } from '@/infrastructure/analytics/app-insights'

interface Props {
  fallbackMessage?: string
}

withDefaults(defineProps<Props>(), {
  fallbackMessage: 'Something went wrong',
})

const error = ref<Error | null>(null)
const route = useRoute()

// Recover on navigation. Without this, a single caught error would latch the boundary into
// its fallback and blank out EVERY subsequent route until a full page reload (F5) — since
// the router-view lives inside this boundary.
watch(
  () => route.fullPath,
  () => {
    error.value = null
  },
)

function onError(err: Error) {
  error.value = err
  console.error('ErrorBoundary caught:', err)
}

// Capture errors thrown by any descendant (render, lifecycle, watchers). Report to
// telemetry, render the fallback, and return false to STOP propagation so Layer 1
// (app.config.errorHandler) does not double-report the same error.
onErrorCaptured((err) => {
  trackException(err, { source: 'boundary' })
  error.value = err instanceof Error ? err : new Error(String(err))
  console.error('ErrorBoundary caught:', err)
  return false
})

function retry() {
  error.value = null
}

defineExpose({ onError })
</script>

<template>
  <div v-if="error">
    <v-alert
      type="error"
      class="ma-4"
    >
      {{ fallbackMessage }}
      <template #append>
        <v-btn
          variant="text"
          size="small"
          @click="retry"
        >
          Retry
        </v-btn>
      </template>
    </v-alert>
  </div>
  <slot v-else />
</template>

<style scoped lang="scss">
</style>
